import { useLayoutEffect } from 'react';
import { syncEffectiveIdentity } from '../lib/identity-scoped-reset';
import type { UserControls } from '@shared/me/wire';

/**
 * Reports the current effective identity to `lib/identity-scoped-reset`, so
 * every module-scope store that holds one identity's data resets together
 * when the signed-in-or-impersonated principal genuinely changes.
 *
 * Called ONCE, by `UserControlsProvider`, with the controls it provides. Module-scope stores have no lifetime
 * tied to any one identity — outliving a provider unmount/remount is exactly
 * what lets a live chat session survive a sidebar close or a tab switch — so
 * a same-tab identity switch (Terra's sign-out has no redirect, so
 * `AuthenticatedLayout` can remount for a different user with no hard page
 * reload) has to be handled explicitly, or the prior identity's data stays
 * visible to whoever is signed in next.
 *
 * Those controls (`useUserControlsContext()` everywhere below the provider)
 * are the effective-identity source the rest of the app already treats as
 * canonical (it is what `/api/me` resolves the
 * impersonated-or-real user to, and what `UserControlsContext`'s Segment
 * `identify()` call keys off) — no new identity-resolution mechanism here.
 * `impersonation.actingAsSlackId` is folded into the key too: every known
 * impersonation-switch path today forces a full page reload (which resets
 * every module store for free), but including it costs nothing and guards a
 * future live-switch admin UI that does not reload.
 *
 * `useLayoutEffect`, not `useEffect`: the prior identity's data must be gone
 * before the new identity's first paint, not after — a passive effect leaves
 * a one-frame flash of the previous principal's data on screen.
 *
 * FIX (2026-08-20, Spencer QA — chats vanished on ordinary navigation),
 * carried over verbatim from the store this hook replaces: `userId`
 * transiently falls back to `null` whenever `useUserControlsContext()`'s
 * `/api/me` query has no cached data yet — not only on first load, but on ANY
 * later queryKey change (a Clerk sessionId flap, or the dev-browser token
 * rotation), which switches to a fresh cache entry with `data === undefined`
 * while its refetch is in flight. A "did it really change" check that treats
 * the unresolved state as a value sees the flap `111:self` → `anon:self` →
 * `111:self` as TWO genuine changes and wipes live state on the first one. An
 * unresolved identity is "don't know yet", not a new principal — so this hook
 * skips the sync ENTIRELY while `userId` is null, and the last KNOWN key
 * survives the gap. A real user switch still resets correctly: the recorded
 * key keeps the prior known value, and the next resolved, genuinely different
 * key fires every registered reset before the new user's first painted frame.
 * Do NOT "simplify" this guard away; it is the fix, not defensiveness.
 */
type EffectiveIdentityInput = Pick<UserControls, 'userId' | 'impersonation'>;

/**
 * The effective-identity key: `null` while `userId` is unresolved ("don't know
 * yet", never a new principal), else `<userId>:<actingAsSlackId | 'self'>`.
 * The one composition every identity-scoped consumer shares.
 */
export function effectiveIdentityKey(
  userId: EffectiveIdentityInput['userId'],
  impersonation: EffectiveIdentityInput['impersonation'],
): string | null {
  return userId == null ? null : `${userId}:${impersonation?.actingAsSlackId ?? 'self'}`;
}

export function useSyncEffectiveIdentity({ userId, impersonation }: EffectiveIdentityInput): void {
  const key = effectiveIdentityKey(userId, impersonation);

  useLayoutEffect(() => {
    if (key === null) return;
    syncEffectiveIdentity(key);
  }, [key]);
}
